/**
 * Date utilities anchored to Adelaide local time (Australia/Adelaide).
 *
 * All dates in Firestore are stored as YYYY-MM-DD strings representing the
 * calendar date in Adelaide time.  Comparing them against UTC midnight or
 * displaying them via UTC Date objects causes up-to-10.5-hour drift, which
 * manifests as "one day behind" for users in the afternoon/evening.
 */

export const ADELAIDE_TZ = 'Australia/Adelaide';

/** Returns today's date as YYYY-MM-DD in Adelaide local time. */
export function todayAdelaide(): string {
  // 'en-CA' locale produces YYYY-MM-DD format natively.
  return new Date().toLocaleDateString('en-CA', { timeZone: ADELAIDE_TZ });
}

/**
 * Formats a stored YYYY-MM-DD date string for Australian display (e.g. "12 Mar 2026").
 * Parses the date components directly to avoid UTC-midnight timezone shift.
 */
export function formatDateAU(value: string | undefined | null): string {
  if (!value) return '—';
  try {
    const [y, m, d] = value.slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return value;
    // Constructing with (year, month-1, day) creates a local-time date, no UTC shift.
    return new Date(y, m - 1, d).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

/**
 * Returns true if the date string (YYYY-MM-DD) is strictly before today in
 * Adelaide timezone.  Lexicographic comparison works correctly for ISO dates.
 */
export function isDateOverdue(dateStr: string | undefined | null): boolean {
  if (!dateStr) return false;
  return dateStr.slice(0, 10) < todayAdelaide();
}

/**
 * Number of full calendar days the date is past due (0 if not overdue or today).
 * Uses Adelaide-local calendar dates so the count is always correct regardless
 * of what time of day it is.
 */
export function daysOverdueCount(dateStr: string | undefined | null): number {
  if (!dateStr) return 0;
  const today = todayAdelaide();
  const ds = dateStr.slice(0, 10);
  if (ds >= today) return 0;
  const [dy, dm, dd] = ds.split('-').map(Number);
  const [ty, tm, td] = today.split('-').map(Number);
  // Build dates with no timezone component so diff is purely in local days.
  const due = new Date(dy, dm - 1, dd);
  const now = new Date(ty, tm - 1, td);
  return Math.floor((now.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
}
